import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import dayjs from 'dayjs';

interface DataIntegrityIssue {
  type: 'orphaned_appointment';
  severity: 'high' | 'medium' | 'low';
  sessionNoteId?: string;
  appointmentId?: string;
  clientId?: string;
  counsellorName?: string;
  sessionDate?: string;
  description: string;
  canAutoFix: boolean;
}

interface DataIntegrityResponse {
  totalIssues: number;
  issuesByType: {
    orphaned_appointment: number;
  };
  issuesBySeverity: {
    high: number;
    medium: number;
    low: number;
  };
  autoFixable: number;
  issues: DataIntegrityIssue[];
}

const DataIntegrityManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [data, setData] = useState<DataIntegrityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const fetchDataIntegrity = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/data-integrity/session-appointments');
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch data integrity status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataIntegrity();
  }, []);

  const handleFixOrphanedAppointments = async () => {
    setFixing(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post('/api/data-integrity/fix-orphaned-appointments');
      setSuccess(`Successfully fixed ${response.data.fixedCount} orphaned appointment(s)`);
      setConfirmDialogOpen(false);
      // Refresh data
      await fetchDataIntegrity();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fix orphaned appointments');
    } finally {
      setFixing(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'orphaned_appointment':
        return 'Orphaned Appointment';
      default:
        return type;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
          Session Data Integrity
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchDataIntegrity}
          disabled={loading}
          variant="outlined"
          sx={{ 
            color: 'white', 
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {data.totalIssues === 0 ? (
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                    ) : (
                      <WarningIcon color="warning" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="h6" sx={{ color: 'white' }}>Total Issues</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white' }}>{data.totalIssues}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ErrorIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="h6" sx={{ color: 'white' }}>High Severity</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: '#f44336' }}>
                    {data.issuesBySeverity.high}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6" sx={{ color: 'white' }}>Medium Severity</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: '#ff9800' }}>
                    {data.issuesBySeverity.medium}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BuildIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" sx={{ color: 'white' }}>Auto-Fixable</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: '#64b5f6' }}>
                    {data.autoFixable}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Issue Type Summary */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Issues by Type
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`Orphaned Appointments: ${data.issuesByType.orphaned_appointment}`}
                  color="error"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Fix Actions */}
          {data.autoFixable > 0 && (
            <Card sx={{ 
              mb: 3, 
              background: 'rgba(100, 181, 246, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(100, 181, 246, 0.3)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                  Quick Fix Available
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.8)' }}>
                  {data.autoFixable} issue(s) can be automatically fixed. This will link orphaned appointments
                  back to their session notes and update appointment statuses.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<BuildIcon />}
                  onClick={() => setConfirmDialogOpen(true)}
                  disabled={fixing}
                >
                  {fixing ? 'Fixing...' : 'Fix Orphaned Appointments'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Issues Table */}
          {data.issues.length > 0 ? (
            <Card sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                  Issue Details {data.issues.length > 100 && '(Showing first 100)'}
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  '& .MuiTableCell-root': {
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 600,
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Severity</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Counsellor</TableCell>
                        <TableCell>Session Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Auto-Fix</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.issues.map((issue, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip
                              label={issue.severity.toUpperCase()}
                              color={getSeverityColor(issue.severity) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{getTypeLabel(issue.type)}</TableCell>
                          <TableCell>{issue.counsellorName || 'N/A'}</TableCell>
                          <TableCell>
                            {issue.sessionDate
                              ? dayjs(issue.sessionDate).format('DD/MM/YYYY')
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 400 }}>
                              {issue.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {issue.canAutoFix ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <ErrorIcon color="disabled" fontSize="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ) : (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="h6">All Clear!</Typography>
              <Typography variant="body2">
                No data integrity issues found. All session notes and appointments are properly linked.
              </Typography>
            </Alert>
          )}
        </>
      ) : null}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Fix Orphaned Appointments?</DialogTitle>
        <DialogContent>
          <Typography>
            This will automatically link {data?.autoFixable} orphaned appointment(s) back to their
            session notes and update their status to "completed".
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            This action cannot be undone, but it's safe and will restore data integrity.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={fixing}>
            Cancel
          </Button>
          <Button
            onClick={handleFixOrphanedAppointments}
            variant="contained"
            color="primary"
            disabled={fixing}
            startIcon={fixing ? <CircularProgress size={20} /> : <BuildIcon />}
          >
            {fixing ? 'Fixing...' : 'Fix Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataIntegrityManagement;
